import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "../../lib/api";
import { crmApi } from "../../lib/crm-api";
import { queryKeys } from "../../lib/queryKeys";
import type { CompanyImportReport } from "../../lib/crm-types";
import { Button } from "../ui/Button";
import { Overlay } from "../ui/Overlay";
import { cn } from "../ui/cn";

/**
 * Create companies from a spreadsheet.
 *
 * **Two presses, and the first one writes nothing.** Preview runs the whole
 * import on the server -- the same parse, the same conflict resolution, the
 * same report -- and stops before the writes, so what is on screen is what
 * will happen rather than an estimate of it. A bulk create has no undo, and
 * the commonest way to get one wrong is a column that meant something else;
 * seeing "line 2: Wilson Hart & Co" before pressing the second button is what
 * catches that.
 *
 * Paste or pick a file, because both are how people have the data. A
 * spreadsheet open on screen is three keystrokes to paste and a saved export
 * is one click to choose, and neither is worth making somebody do the other
 * way round.
 *
 * Nothing about the columns is configured here. `company-import.ts` on the
 * server accepts the aliases these files actually carry -- "Trade Account",
 * "account_number", "Acct No" -- which is a better answer than a mapping step
 * somebody has to complete before they can find out whether it was needed.
 */
export function ImportCompaniesDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [csv, setCsv] = useState("");
  const [fileName, setFileName] = useState<string | null>(null);
  const [report, setReport] = useState<CompanyImportReport | null>(null);
  const filePicker = useRef<HTMLInputElement>(null);

  const run = useMutation({
    mutationFn: (apply: boolean) => crmApi.importCompanies(csv, apply),
    onSuccess: (result) => {
      setReport(result);
      if (!result.applied) return;
      /* The list, the filters and the duplicate queue have all just changed. */
      void queryClient.invalidateQueries({ queryKey: queryKeys.crmCompaniesRoot });
      void queryClient.invalidateQueries({ queryKey: ["crm", "duplicates"] });
    },
  });

  const read = (file: File): void => {
    const reader = new FileReader();
    reader.onload = () => {
      setCsv(String(reader.result ?? ""));
      setFileName(file.name);
      /* The old report described the old file. Leaving it up would invite
         somebody to press Import against a preview of something else. */
      setReport(null);
    };
    reader.readAsText(file);
  };

  const done = report?.applied === true;
  const ready = csv.trim().length > 0;

  return (
    <Overlay
      onClose={onClose}
      title="Import companies"
      size="xl"
      footer={
        <>
          {run.isError ? (
            <span className="mr-auto text-xs text-sla-breached">
              {run.error instanceof ApiError
                ? run.error.message
                : "Could not read that file."}
            </span>
          ) : null}

          <Button variant="secondary" onClick={onClose}>
            {done ? "Close" : "Cancel"}
          </Button>

          {done ? null : report ? (
            <Button
              disabled={run.isPending || report.created.length === 0}
              onClick={() => run.mutate(true)}
            >
              {run.isPending
                ? "Importing…"
                : `Import ${report.created.length} ${
                    report.created.length === 1 ? "company" : "companies"
                  }`}
            </Button>
          ) : (
            <Button disabled={!ready || run.isPending} onClick={() => run.mutate(false)}>
              {run.isPending ? "Reading…" : "Preview"}
            </Button>
          )}
        </>
      }
    >
      {done ? null : (
        <>
          <p className="text-xs text-muted">
            A CSV with a <span className="text-content">Name</span> column.
            Domain, VAT number, trade account, company number, email, phone,
            city and region are read when they are there, under whatever
            heading your export uses.
          </p>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <input
              ref={filePicker}
              type="file"
              accept=".csv,text/csv,text/plain"
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) read(file);
                /* Cleared so choosing the same file twice fires again --
                   which is what somebody does after fixing it in Excel. */
                event.target.value = "";
              }}
            />
            <Button
              size="sm"
              variant="secondary"
              onClick={() => filePicker.current?.click()}
            >
              Choose a file
            </Button>
            <span className="text-xs text-faint">
              {fileName ?? "or paste the rows below"}
            </span>
          </div>

          <textarea
            value={csv}
            onChange={(event) => {
              setCsv(event.target.value);
              setFileName(null);
              setReport(null);
            }}
            rows={8}
            spellCheck={false}
            placeholder={"Name,Domain,Trade Account\nWilson Hart & Co,wilsonhart.co.uk,NX-9001"}
            aria-label="Paste the spreadsheet rows"
            className="mt-2 w-full rounded-md border border-line bg-app px-2 py-1.5 font-mono text-xs text-content outline-none focus:border-brand-400"
          />
        </>
      )}

      {report ? <Report report={report} /> : null}
    </Overlay>
  );
}

/** What happened, or what would. */
function Report({ report }: { report: CompanyImportReport }) {
  return (
    <div className={cn(report.applied ? "" : "mt-4 border-t border-line pt-3")}>
      <p className="text-sm text-content">
        {report.applied
          ? `Imported ${report.created.length} of ${report.total}.`
          : `Read ${report.total} ${report.total === 1 ? "row" : "rows"} — ${report.created.length} would be created.`}
      </p>
      <p className="mt-0.5 text-[11px] text-faint">
        Columns read: {report.headers.join(", ") || "none"}
      </p>

      {/* Skipped and failed first, because they are the part somebody has to
          do something about. What went in needs no action. */}
      <Rows
        title={`Already in the CRM — ${report.skipped.length} left alone`}
        tone="warning"
        rows={report.skipped}
      />
      <Rows
        title={`Could not be read — ${report.failed.length}`}
        tone="breached"
        rows={report.failed}
      />
      <Rows
        title={
          report.applied
            ? `Created — ${report.created.length}`
            : `Would be created — ${report.created.length}`
        }
        tone="ok"
        rows={report.created.map((row) => ({ ...row, reason: "" }))}
      />
    </div>
  );
}

function Rows({
  title,
  tone,
  rows,
}: {
  title: string;
  tone: "ok" | "warning" | "breached";
  rows: { line: number; name: string; reason: string }[];
}) {
  if (rows.length === 0) return null;

  return (
    <div className="mt-3">
      <p
        className={cn(
          "text-xs font-medium",
          tone === "ok" && "text-sla-ok",
          tone === "warning" && "text-sla-warning",
          tone === "breached" && "text-sla-breached",
        )}
      >
        {title}
      </p>
      <ul className="mt-1 max-h-48 overflow-y-auto">
        {rows.map((row) => (
          <li
            key={`${row.line}-${row.name}`}
            className="flex flex-wrap items-baseline gap-x-2 border-b border-line/60 py-1 text-xs last:border-b-0"
          >
            {/* The line in their file, header counted, so it names the row
                they can see on screen. */}
            <span className="w-10 shrink-0 tabular-nums text-faint">
              {row.line}
            </span>
            <span className="min-w-0 text-content">{row.name || "—"}</span>
            {row.reason ? (
              <span className="text-[11px] text-muted">{row.reason}</span>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}
